import React, { useMemo, useState } from 'react';
import { LabelRuleForm, PageDefinitionForm } from '../../types';
import {
  Box,
  IconButton,
  InputDescription,
  InputLabel,
  MultiSelectMenu,
  OptionType,
  TextArea,
  TextInput,
  useSelectMenu,
  Popover,
  Divider,
  Text,
  Pane,
  Flex,
  DeprecatedIcon,
  Button,
  Icon,
} from '@fullstory/components';
import { PageAPIDefinition } from '../PageAPIDefinition';
import { PageDefinition } from '@fullstory/proto/fs/api/pages';
import { AutocompleteRequest, ValidationError } from '@fullstory/proto/fs/services/paige';
import { URLRules } from '../URLRules';
import { FormattedMessage, useIntl } from 'react-intl';
import { useFlags } from '@fullstory/react-hooks';
import { DetailModalPropertiesTable, PropertyRow } from '@fullstory/events-ui';
import { EMPTY_PATTERN, Mode } from '../../constants';
import { Issues, PageIssueSilence } from '@fullstory/proto/fs/services/dx-objects';
import { IssuesBanner, SnoozeUpdate } from '@fullstory/dqm-ui';
import { useAiPageLabelSuggestions } from '../../hooks/useAiPageLabelSuggestion';
import { formPatterns, validatePattern } from '../../utils/form';
import css from '../URLRules/index.module.scss';
import { assertExhaustive } from '@fullstory/typeutils';
import { URLRuleRow } from '../URLRules/URLRulesPatterns';
import { ConflictingRules } from '../ConflictingRules';
import { SpecObjectInfo } from '@fullstory/redux';

type PageDefFormProps = {
  form: PageDefinitionForm;
  mode: (typeof Mode)[keyof typeof Mode];
  setForm: (form: PageDefinitionForm) => void;
  handleChange: (
    key: 'name' | 'description',
  ) => (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
  errors?: ValidationError[];
  hostnameOptions: string[];
  getSuggestions: (
    path: string,
    fragment: string,
    hostname: string,
    scope: AutocompleteRequest.Scope,
  ) => Promise<string[]>;
  getPageById: (pageDefId: number) => PageDefinition | undefined;
  onConflictingRulesEdit: (pageId: number | null) => void;
  onConflictingRulesArchive: (pageId: number) => Promise<void>;
  issues: Issues.Issue[];
  snoozed: PageIssueSilence | null;
  setSnooze: (snoozed: SnoozeUpdate) => void;
  propertiesProps?: {
    properties: PropertyRow[];
    propertiesLoading: boolean;
    propertiesFailed: boolean;
    retryFailedProperties: () => void;
    canArchiveProperties: boolean;
  };
  selectedFsSpecObjectSlugs: OptionType<string>[] | null;
  setSelectedFsSpecObjectSlugs: (slugs: OptionType<string>[] | null) => void;
  fsSpecEnabled: boolean;
  fsSpecRoleOptions: OptionType<string>[];
  existingSpecObjects: SpecObjectInfo[];
  pageDefOnTop?: boolean;
  preventOverflow?: boolean;
};

export const PageDefForm = ({
  form,
  setForm,
  handleChange,
  errors,
  hostnameOptions,
  getSuggestions,
  mode,
  getPageById,
  propertiesProps,
  onConflictingRulesEdit,
  onConflictingRulesArchive,
  selectedFsSpecObjectSlugs,
  setSelectedFsSpecObjectSlugs,
  fsSpecEnabled,
  fsSpecRoleOptions,
  existingSpecObjects,
  pageDefOnTop,
  issues,
  snoozed,
  setSnooze,
  preventOverflow,
}: PageDefFormProps) => {
  const flags = useFlags();
  const isAiSuggestionsEnabled = flags.featureFlags().AppFsSpecAi;
  const mergedPageSupport = flags.featureFlags().AppMergedPageSupport;
  const isCaseInsensitive = flags.featureFlags().PaigeCaseInsensitiveMatching;
  const intl = useIntl();
  const [suggestionsOpen, setSuggestionsOpen] = useState(false);

  // name options get filtered as the user types, and should not show already used spec objects
  const nameOptions = useMemo(
    () =>
      fsSpecRoleOptions.filter(
        option =>
          option.label.toLocaleLowerCase().includes(form.name.value.toLocaleLowerCase()) &&
          !option.disabled,
      ),
    [fsSpecRoleOptions, form.name.value],
  );

  // select menu for name suggestions
  const { Options, optionsProps } = useSelectMenu({
    testId: 'page-modal-name-suggestions',
    options: nameOptions,
    preventOverflow: preventOverflow ? 16 : undefined,
    flipY: preventOverflow || undefined,
    onChange: option => {
      if (option) {
        setForm({ ...form, name: { value: option.label } });
        setSelectedFsSpecObjectSlugs([option]);
        setSuggestionsOpen(false);
      }
    },
  });

  const {
    suggestions: options,
    loadSuggestions,
    loading,
    isEnabled: aiLabelsEnabled,
  } = useAiPageLabelSuggestions({
    patterns: formPatterns(form),
  });

  const [popoverAnchor, setPopoverAnchor] = useState<HTMLElement | null>(null);
  // select menu for AI name suggestions
  const { Options: OptionsAi, optionsProps: optionsPropsAi } = useSelectMenu({
    testId: 'page-modal-name-suggestions-ai',
    options,
    onChange: option => {
      if (option && option.label) {
        if (option.value.specSlug) {
          setSelectedFsSpecObjectSlugs([
            {
              label: option.value.specSlug,
              value: option.value.specSlug,
              key: option.value.specSlug,
            },
          ]);
        }
        setForm({
          ...form,
          name: { value: option.value.name },
          description: { value: option.value.description },
        });
        setSuggestionsOpen(false);
      }
    },
  });

  // show url rules if we have patterns or if there are no labels (i.e. we're creating and everything is empty)
  const showUrlRules = useMemo(
    () => !form.definition.value.length || form.definition.value.some(v => v.type === 'pattern'),
    [form.definition.value],
  );

  const definitionLabels = useMemo(
    () => form.definition.value.filter((v): v is LabelRuleForm => v.type === 'label'),
    [form.definition.value],
  );
  const hasApiPageName = !!definitionLabels.length;

  const hasNonEmptyPath = useMemo(
    () => form.definition.value.some(v => v.type === 'pattern' && v.path.value.length > 0),
    [form.definition.value],
  );

  return (
    <>
      {(issues.length > 0 || snoozed) && (
        <Box paddingBottom={16}>
          <IssuesBanner
            objectType={intl.formatMessage({ defaultMessage: 'page', id: 'ovCNnF' })}
            setSnooze={setSnooze}
            snoozed={snoozed}
          />
        </Box>
      )}
      <Box
        display="flex"
        flexDirection={pageDefOnTop && mode === Mode.create ? 'column' : 'column-reverse'}
      >
        <Box paddingBottom={24}>
          <Box marginBottom={4}>
            <Text appearance="bodyMedium" color="emphasized">
              <FormattedMessage defaultMessage="Page Definition" id="HVW24f" />
            </Text>
          </Box>
          <Box display="flex" flexDirection="column" gap={16}>
            {mergedPageSupport ? (
              <>
                {!!errors?.length && (
                  <ConflictingRules
                    errors={errors}
                    onEdit={onConflictingRulesEdit}
                    onArchive={onConflictingRulesArchive}
                    getPageById={getPageById}
                  />
                )}
                {form.definition.value.map((v, idx) => {
                  switch (v.type) {
                    case 'label':
                      return (
                        <PageAPIDefinition
                          idx={idx}
                          pageName={v.pageName}
                          setPageName={
                            v.editable
                              ? (pageName: string) => {
                                  setForm({
                                    ...form,
                                    definition: {
                                      ...form.definition,
                                      value: form.definition.value.map((rule, i) =>
                                        i === idx ? { ...rule, pageName } : rule,
                                      ),
                                    },
                                  });
                                }
                              : undefined
                          }
                          platforms={v.platforms}
                          onRemove={
                            form.definition.value.length > 1
                              ? () => {
                                  setForm({
                                    ...form,
                                    definition: {
                                      ...form.definition,
                                      value: form.definition.value.filter((_, i) => i !== idx),
                                    },
                                  });
                                }
                              : undefined
                          }
                        />
                      );
                    case 'pattern':
                      return (
                        <URLRuleRow
                          rule={v}
                          idx={idx}
                          onChange={v => {
                            setForm({
                              ...form,
                              definition: {
                                ...form.definition,
                                value: form.definition.value.map((rule, i) =>
                                  i === idx ? validatePattern(v, flags) : rule,
                                ),
                              },
                            });
                          }}
                          onRemove={
                            form.definition.value.length > 1
                              ? () => {
                                  setForm({
                                    ...form,
                                    definition: {
                                      ...form.definition,
                                      value: form.definition.value.filter((_, i) => i !== idx),
                                    },
                                  });
                                }
                              : undefined
                          }
                          hostnameOptions={hostnameOptions}
                          getSuggestions={getSuggestions}
                          validationErrors={errors}
                          isML={!form.isUserDefined}
                          isCaseInsensitive={isCaseInsensitive}
                          preventOverflow={preventOverflow}
                        />
                      );
                    default:
                      assertExhaustive(v);
                  }
                })}
                <Flex justifyContent="flex-start">
                  <Button
                    variant="outline"
                    onClick={() => {
                      setForm({
                        ...form,
                        definition: {
                          ...form.definition,
                          value: [...form.definition.value, EMPTY_PATTERN],
                        },
                      });
                    }}
                  >
                    <Flex gap={8}>
                      <Icon name="plus" />
                      <FormattedMessage
                        defaultMessage="Add URL rule"
                        id="QNpOoT"
                        description="Add a URL rule to the page definition."
                      />
                    </Flex>
                  </Button>
                  {!hasApiPageName && (
                    <Button
                      variant="minimal"
                      onClick={() => {
                        setForm({
                          ...form,
                          definition: {
                            ...form.definition,
                            value: [
                              ...form.definition.value,
                              {
                                type: 'label',
                                pageName: '',
                                platforms: [],
                                editable: true,
                              },
                            ],
                          },
                        });
                      }}
                    >
                      <FormattedMessage
                        defaultMessage="Add API page name"
                        id="zHBkCi"
                        description="Add an API page name to the page definition."
                      />
                    </Button>
                  )}
                </Flex>
              </>
            ) : (
              <>
                {definitionLabels.map((label, idx) => (
                  <PageAPIDefinition
                    idx={idx}
                    key={label.pageName}
                    pageName={label.pageName}
                    platforms={label.platforms}
                  />
                ))}
                {showUrlRules && (
                  <URLRules
                    onConflictingRulesEdit={onConflictingRulesEdit}
                    onConflictingRulesArchive={onConflictingRulesArchive}
                    errors={errors}
                    setForm={setForm}
                    form={form}
                    hostnameOptions={hostnameOptions}
                    getSuggestions={getSuggestions}
                    mode={mode}
                    getPageById={getPageById}
                    isCaseInsensitive={isCaseInsensitive}
                    preventOverflow={preventOverflow}
                  />
                )}
              </>
            )}
            {showUrlRules && (
              <Pane padding={12} borderRadius={4} className={css.warningBanner}>
                <Flex justifyContent="flex-start">
                  <Box padding={[0, 12, 0, 4]}>
                    <DeprecatedIcon name="information-primary" color="yellow-base" size={16} />
                  </Box>
                  <Text appearance="bodyMedium">
                    <FormattedMessage
                      defaultMessage="Please note that {mode, select, Edit {changes to} other {new}} URL rules are applied to future sessions."
                      id="KUMHkl"
                      values={{ mode }}
                    />
                  </Text>
                </Flex>
              </Pane>
            )}
          </Box>
        </Box>
        <Box>
          <Box paddingBottom={24} ref={setPopoverAnchor}>
            <InputLabel htmlFor="name">
              <FormattedMessage defaultMessage="Name" id="HAlOn1" />
            </InputLabel>
            <TextInput
              id="name"
              placeholder={intl.formatMessage({
                defaultMessage: 'Name this Page...',
                id: 'r5VfuB',
              })}
              value={form.name.value}
              invalid={!!form.name.error}
              onChange={handleChange('name')}
              autoComplete="off"
              // automatically open name suggestions only if there's no name and there's a url path
              onFocus={() => {
                if (!form.name.value && hasNonEmptyPath && fsSpecEnabled) setSuggestionsOpen(true);
              }}
              rightAdornment={
                hasNonEmptyPath && aiLabelsEnabled ? (
                  <IconButton
                    iconColor="ai"
                    size="large"
                    title={intl.formatMessage({ defaultMessage: 'Suggest name', id: 'KHtUXF' })}
                    iconName={loading ? 'ai-animated' : 'ai'}
                    onClick={() => {
                      loadSuggestions();
                      setSuggestionsOpen(true);
                    }}
                  />
                ) : undefined
              }
            />
            {form.name.error && <InputDescription invalid description={form.name.error} />}
          </Box>
          <Popover
            anchor={popoverAnchor}
            onCloseSignal={() => setSuggestionsOpen(false)}
            open={suggestionsOpen}
            variant="bordered"
            testId="page-modal-name-suggestions"
          >
            <Box maxHeight={385} overflow="auto">
              {isAiSuggestionsEnabled && (
                <>
                  <OptionsAi {...optionsPropsAi} />
                  <Divider />
                </>
              )}
              <Options {...optionsProps} />
            </Box>
          </Popover>
          {fsSpecEnabled && fsSpecRoleOptions.length > 0 && (
            <Box paddingBottom={16} testId="fs-spec-role-select">
              <InputLabel id="Role" optional>
                <FormattedMessage defaultMessage="Role" id="1ZgrhW" />
              </InputLabel>
              <MultiSelectMenu
                menuHeight={385}
                variant="fill-builder"
                options={fsSpecRoleOptions}
                onChange={options => setSelectedFsSpecObjectSlugs(options)}
                selected={selectedFsSpecObjectSlugs || []}
                filterable
                testId="named-element-modal-role-multi-select"
                preventOverflow={preventOverflow ? 16 : undefined}
                flipY={preventOverflow || undefined}
              />
            </Box>
          )}
          <Box paddingBottom={24}>
            <InputLabel optional htmlFor="description">
              <FormattedMessage defaultMessage="Description" id="Q8Qw5B" />
            </InputLabel>
            <TextArea
              id="description"
              placeholder={intl.formatMessage({
                defaultMessage:
                  'Add a short description that can help other users understand what this Page refers to...',
                id: 'Ew0whO',
              })}
              value={form.description.value}
              onChange={handleChange('description')}
            />
          </Box>
        </Box>
      </Box>
      {propertiesProps && propertiesProps.properties.length > 0 && (
        <Box>
          <DetailModalPropertiesTable
            properties={propertiesProps.properties}
            connectionParent={form.id}
            loading={propertiesProps.propertiesLoading}
            failed={propertiesProps.propertiesFailed}
            retryFailed={propertiesProps.retryFailedProperties}
            canArchive={propertiesProps.canArchiveProperties}
            existingSpecObjects={existingSpecObjects}
          />
        </Box>
      )}
    </>
  );
};
